import { useEffect, useMemo, useState } from 'react';
import { Download, MessageSquare, MessageSquareText } from 'lucide-react';
import type { Transaction, TransactionStatus } from '@app/shared';
import { cn } from 'cn';
import { toast } from 'sonner';
import { useUpdateTransactionNote } from '@/features/assignment/queries';
import { useProperties } from '@/features/properties/queries';
import { useCategories } from '@/features/reference/queries';
import { apiErrorMessage } from '@/lib/api';
import { formatCents, formatIsoDate } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TablePagination } from '@/components/ui/table-pagination';
import { Textarea } from '@/components/ui/textarea';
import { usePagination } from '@/hooks/use-pagination';
import { downloadCsv, transactionsToCsv } from './csv';
import { LedgerTabs } from './LedgerTabs';
import { useTransactions } from './queries';

const STATUS_LABELS: Record<TransactionStatus, string> = {
  action_required: 'Action requise',
  validated: 'Validée',
};

const ALL = '__all__';

function NoteDialog({ transaction, onClose }: { transaction: Transaction; onClose: () => void }) {
  const [note, setNote] = useState(transaction.note ?? '');
  const updateNote = useUpdateTransactionNote();

  function save() {
    const trimmed = note.trim();
    updateNote.mutate(
      { id: transaction.id, input: { note: trimmed === '' ? null : trimmed } },
      { onSuccess: onClose, onError: (error) => toast.error(apiErrorMessage(error, 'Impossible d’enregistrer la note.')) },
    );
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Note — {transaction.label}</DialogTitle>
        </DialogHeader>
        <Textarea
          value={note}
          onChange={(event) => setNote(event.target.value)}
          maxLength={500}
          rows={4}
          placeholder="Ex. Achat Tedee Go 2 Pro"
          autoFocus
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={save} disabled={updateNote.isPending}>Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TransactionsPage() {
  const [propertyId, setPropertyId] = useState<string>(ALL);
  const [categoryId, setCategoryId] = useState<string>(ALL);
  const [status, setStatus] = useState<string>(ALL);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [editingNoteFor, setEditingNoteFor] = useState<Transaction | null>(null);

  const properties = useProperties();
  const categories = useCategories();

  const filters = useMemo(
    () => ({
      propertyId: propertyId === ALL ? undefined : Number(propertyId),
      categoryId: categoryId === ALL ? undefined : Number(categoryId),
      status: status === ALL ? undefined : (status as TransactionStatus),
      dateFrom: dateFrom === '' ? undefined : dateFrom,
      dateTo: dateTo === '' ? undefined : dateTo,
      search: search.trim() === '' ? undefined : search.trim(),
    }),
    [propertyId, categoryId, status, dateFrom, dateTo, search],
  );

  const transactions = useTransactions(filters);
  const { page, pageCount, start, end, setPage } = usePagination(transactions.data?.length ?? 0);
  useEffect(() => setPage(1), [filters, setPage]);

  const propertyNames = useMemo(() => new Map(properties.data?.map((p) => [p.id, p.name])), [properties.data]);
  const categoryLabels = useMemo(() => new Map(categories.data?.map((c) => [c.id, c.label])), [categories.data]);

  function exportCsv() {
    if (!transactions.data) return;
    const csv = transactionsToCsv(transactions.data, propertyNames, categoryLabels);
    downloadCsv(`transactions-${new Date().toISOString().slice(0, 10)}.csv`, csv);
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Ledger</h1>
        <Button variant="outline" onClick={exportCsv} disabled={!transactions.data || transactions.data.length === 0}>
          <Download /> Export CSV
        </Button>
      </div>
      <LedgerTabs />

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Bien</span>
          <Select value={propertyId} onValueChange={setPropertyId}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tous les biens</SelectItem>
              {properties.data?.map((property) => (
                <SelectItem key={property.id} value={String(property.id)}>{property.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Catégorie</span>
          <Select value={categoryId} onValueChange={setCategoryId}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Toutes les catégories</SelectItem>
              {categories.data?.map((category) => (
                <SelectItem key={category.id} value={String(category.id)}>{category.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Statut</span>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tous les statuts</SelectItem>
              <SelectItem value="validated">{STATUS_LABELS.validated}</SelectItem>
              <SelectItem value="action_required">{STATUS_LABELS.action_required}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Du</span>
          <Input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} className="w-40" />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Au</span>
          <Input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} className="w-40" />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Recherche</span>
          <Input
            type="search"
            placeholder="Libellé…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="w-48"
          />
        </div>
      </div>

      {transactions.isPending && <p className="text-muted-foreground">Chargement…</p>}
      {transactions.isError && <p className="text-destructive">Impossible de charger les écritures.</p>}
      {transactions.data?.length === 0 && <p className="text-muted-foreground">Aucune écriture pour ces filtres.</p>}

      {transactions.data && transactions.data.length > 0 && (
        <Card className="gap-0 overflow-hidden py-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/60 hover:bg-muted/60">
              <TableHead className="px-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:px-2">
                Date
              </TableHead>
              <TableHead className="px-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:px-2">
                Libellé
              </TableHead>
              <TableHead className="hidden px-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:table-cell sm:px-2">
                Bien
              </TableHead>
              <TableHead className="hidden px-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:table-cell sm:px-2">
                Catégorie
              </TableHead>
              <TableHead className="hidden px-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:table-cell sm:px-2">
                Statut
              </TableHead>
              <TableHead className="px-1.5 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:px-2">
                Montant
              </TableHead>
              <TableHead className="px-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:px-2">
                Note
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {transactions.data.slice(start, end).map((tx) => {
              const categoryLabel = tx.categoryId === null ? null : (categoryLabels.get(tx.categoryId) ?? String(tx.categoryId));
              return (
                <TableRow key={tx.id}>
                  <TableCell className="px-1.5 text-muted-foreground sm:px-2">{formatIsoDate(tx.txDate)}</TableCell>
                  <TableCell className="max-w-24 truncate px-1.5 sm:max-w-xs sm:px-2" title={tx.label}>
                    {tx.label}
                  </TableCell>
                  <TableCell className="hidden px-1.5 sm:table-cell sm:px-2">
                    {tx.propertyId === null ? '—' : (propertyNames.get(tx.propertyId) ?? tx.propertyId)}
                  </TableCell>
                  <TableCell className="hidden px-1.5 sm:table-cell sm:px-2">
                    {categoryLabel ? (
                      <span className="inline-flex w-fit items-center rounded-md bg-accent px-2 py-1 text-xs font-semibold text-accent-foreground">
                        {categoryLabel}
                      </span>
                    ) : (
                      <span className="inline-flex w-fit items-center rounded-md border border-dashed border-input px-2 py-1 text-xs font-medium text-muted-foreground">
                        À catégoriser
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="hidden px-1.5 sm:table-cell sm:px-2">{STATUS_LABELS[tx.status]}</TableCell>
                  <TableCell
                    className={cn(
                      'px-1.5 text-right font-semibold tabular-nums sm:px-2',
                      tx.amountCents < 0 ? 'text-destructive' : 'text-emerald-700',
                    )}
                  >
                    {formatCents(tx.amountCents, 'EUR')}
                  </TableCell>
                  <TableCell className="px-1.5 sm:px-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-7"
                      title={tx.note ?? 'Ajouter une note'}
                      onClick={() => setEditingNoteFor(tx)}
                    >
                      {tx.note ? <MessageSquareText className="text-primary" /> : <MessageSquare className="text-muted-foreground" />}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          page={page}
          pageCount={pageCount}
          start={start}
          end={end}
          total={transactions.data.length}
          onPageChange={setPage}
        />
        </Card>
      )}
      {editingNoteFor && <NoteDialog transaction={editingNoteFor} onClose={() => setEditingNoteFor(null)} />}
    </div>
  );
}
